import React, { useState, useEffect } from "react";
import { ApiKeyOrGetter } from "../../services/GeneralService";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { BillingService } from "../../services/BillingService";
import { PaymentMethod } from "../../model_gen/billing";
import { toast } from "sonner";

interface AutoTopupModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  apiKey: ApiKeyOrGetter;
  currentEnabled: boolean;
  currentThreshold: string;
  currentAmount: string;
  autopayCreditGranted: boolean;
}

const AutoTopupModal: React.FC<AutoTopupModalProps> = ({
  open,
  onClose,
  onSuccess,
  apiKey,
  currentEnabled,
  currentThreshold,
  currentAmount,
  autopayCreditGranted,
}) => {
  const [enabled, setEnabled] = useState<boolean>(currentEnabled);
  const [threshold, setThreshold] = useState<string>(currentThreshold);
  const [amount, setAmount] = useState<string>(currentAmount);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    number | null
  >(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setEnabled(currentEnabled);
      setThreshold(currentThreshold);
      setAmount(currentAmount);
      fetchPaymentMethods();
    }
  }, [open, currentEnabled, currentThreshold, currentAmount]);

  const fetchPaymentMethods = () => {
    setLoading(true);
    BillingService.getPaymentMethods(null, apiKey).subscribe(
      (result: PaymentMethod[]) => {
        setPaymentMethods(result.filter((pm) => pm.status === "ENABLED"));

        // Find the auto-topup payment method or default
        const autoTopupMethod = result.find((pm) => pm.auto_topup_enabled);
        if (autoTopupMethod) {
          setSelectedPaymentMethodId(autoTopupMethod.id || null);
        } else {
          const defaultMethod = result.find((pm) => pm.is_default);
          if (defaultMethod) {
            setSelectedPaymentMethodId(defaultMethod.id || null);
          } else if (result.length > 0) {
            setSelectedPaymentMethodId(result[0].id || null);
          }
        }
        setLoading(false);
      },
      (error: any) => {
        console.error("Error fetching payment methods:", error);
        toast.error("Failed to load payment methods");
        setLoading(false);
      }
    );
  };

  const handleSave = () => {
    if (!enabled) {
      // Disable auto top-up
      const paymentMethodToUpdate =
        selectedPaymentMethodId ||
        paymentMethods.find((pm) => pm.auto_topup_enabled)?.id;

      if (paymentMethodToUpdate) {
        setSaving(true);
        BillingService.configureAutoTopup(
          paymentMethodToUpdate,
          false,
          0,
          0,
          null,
          apiKey
        ).subscribe(
          () => {
            toast.success("Auto top-up disabled");
            setSaving(false);
            onSuccess();
            onClose();
          },
          (error: any) => {
            console.error("Error disabling auto top-up:", error);
            toast.error("Failed to disable auto top-up");
            setSaving(false);
          }
        );
      }
      return;
    }

    // Validate inputs
    if (!selectedPaymentMethodId) {
      toast.error("Please select a payment method");
      return;
    }

    const thresholdNum = parseFloat(threshold);
    const amountNum = parseFloat(amount);

    if (isNaN(thresholdNum) || thresholdNum <= 0) {
      toast.error("Threshold must be greater than $0");
      return;
    }

    if (isNaN(amountNum) || amountNum < 10) {
      toast.error("Top-up amount must be at least $10");
      return;
    }

    setSaving(true);

    BillingService.configureAutoTopup(
      selectedPaymentMethodId,
      true,
      thresholdNum,
      amountNum,
      null,
      apiKey
    ).subscribe(
      () => {
        toast.success("Auto top-up settings saved");
        setSaving(false);
        onSuccess();
        onClose();
      },
      (error: any) => {
        console.error("Error saving auto top-up:", error);
        toast.error("Failed to save auto top-up settings");
        setSaving(false);
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-gc-demi-bold">
            Auto recharge settings
          </DialogTitle>
        </DialogHeader>

        {!currentEnabled && !autopayCreditGranted && (
          <div className="px-4 py-3 bg-maitai-green/10 border border-maitai-green/20 rounded-lg">
            <p className="text-sm font-gc-med text-foreground">
              Set up autopay, get $5 in credits
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Enable auto recharge and receive $5 in credits to keep your
              service running smoothly.
            </p>
          </div>
        )}

        <div className="space-y-6 py-4">
          {/* Enable checkbox */}
          <div className="space-y-3">
            <Label className="text-sm font-gc-med text-foreground">
              Would you like to set up automatic recharge?
            </Label>
            <div className="flex items-start gap-3">
              <Checkbox
                id="enable-auto-topup"
                checked={enabled}
                onCheckedChange={(checked) => setEnabled(checked as boolean)}
              />
              <label
                htmlFor="enable-auto-topup"
                className="text-sm text-foreground cursor-pointer leading-tight">
                Yes, automatically recharge my card when my credit balance falls
                below a threshold
              </label>
            </div>
          </div>

          {enabled && (
            <>
              {/* Threshold */}
              <div className="space-y-2">
                <Label className="text-sm font-gc-med text-foreground">
                  When credit balance goes below
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                    $
                  </span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={threshold}
                    onChange={(e) => setThreshold(e.target.value)}
                    className="pl-6"
                    disabled={saving}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Trigger auto top-up when balance falls below this amount
                </p>
              </div>

              {/* Top-up amount */}
              <div className="space-y-2">
                <Label className="text-sm font-gc-med text-foreground">
                  Bring credit balance back up to
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                    $
                  </span>
                  <Input
                    type="number"
                    step="0.01"
                    min="10"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="pl-6"
                    disabled={saving}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Minimum $10. This amount will be charged to your card.
                </p>
              </div>

              {/* Payment method */}
              {!loading && paymentMethods.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-gc-med text-foreground">
                    Payment method
                  </Label>
                  <select
                    value={selectedPaymentMethodId || ""}
                    onChange={(e) =>
                      setSelectedPaymentMethodId(parseInt(e.target.value))
                    }
                    disabled={saving}
                    className="w-full h-10 px-3 text-sm rounded-md border border-border bg-background">
                    {paymentMethods.map((pm) => (
                      <option key={pm.id} value={pm.id || ""}>
                        {pm.meta.card?.brand} ••••{pm.meta.card?.last4}
                        {pm.is_default ? " (Default)" : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving ? "Saving..." : "Save settings"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AutoTopupModal;
